import {
  Directive,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Output,
  OutputRefSubscription,
  effect,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { NgpRadioGroup, injectRadioGroupState } from 'ng-primitives/radio';
import { SuiFormControlBase } from '../shared/cva-helper';
import {
  SuiFormFieldControl,
  SUI_FORM_FIELD_CONTROL,
} from '../form-field/form-field-control';
import { SuiRadioValue } from './sui-radio.component';

/**
 * Standalone value accessor for the radio group.
 *
 * Why this exists: `SuiRadioGroupDirective` hosts `NgpRadioGroup` as a
 * hostDirective, and `NgpRadioGroup` injects `NgControl` in its constructor
 * (via `setupFormControl`). If the directive itself were the `NG_VALUE_ACCESSOR`
 * (`useExisting`), then `NgControl` → value accessor (the directive) →
 * `NgpRadioGroup` (host) → `NgControl` would be a construction cycle → NG0200.
 *
 * This leaf object is the value accessor instead. It resolves the directive
 * **lazily** (only when the form first talks to it, after construction), so
 * `NgControl`'s dependency graph never loops back into `NgpRadioGroup`. Unlike
 * the other sui controls (whose ng-primitives directive sits on a template
 * child and is constructed late), the radio group's primitive is on the host,
 * so it needs this indirection.
 */
export class SuiRadioGroupValueAccessor implements ControlValueAccessor {
  private readonly injector = inject(Injector);
  private _target?: SuiRadioGroupDirective;

  private get target(): SuiRadioGroupDirective {
    return (this._target ??= this.injector.get(SuiRadioGroupDirective));
  }

  writeValue(value: SuiRadioValue | null): void {
    this.target.writeValue(value);
  }
  registerOnChange(fn: (value: SuiRadioValue | null) => void): void {
    this.target.registerOnChange(fn);
  }
  registerOnTouched(fn: () => void): void {
    this.target.registerOnTouched(fn);
  }
  setDisabledState(isDisabled: boolean): void {
    this.target.setDisabledState(isDisabled);
  }
}

@Directive({
  selector: '[sui-radio-group]',
  standalone: true,
  hostDirectives: [
    {
      directive: NgpRadioGroup,
      inputs: ['ngpRadioGroupOrientation: orientation'],
    },
  ],
  host: {
    '[attr.aria-invalid]': "errorState ? 'true' : null",
    '(pointerdown)': 'markUserInitiated()',
    '(keydown)': 'markUserInitiated()',
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useClass: SuiRadioGroupValueAccessor,
      multi: true,
    },
    {
      provide: SUI_FORM_FIELD_CONTROL,
      useExisting: forwardRef(() => SuiRadioGroupDirective),
    },
  ],
})
export class SuiRadioGroupDirective
  extends SuiFormControlBase<SuiRadioValue>
  implements SuiFormFieldControl, OnInit, OnDestroy
{
  private readonly ngpRadioGroup = inject(NgpRadioGroup);
  private readonly radioGroupState = injectRadioGroupState();
  private subscription?: OutputRefSubscription;
  private _focused = false;
  private _userInitiated = false;

  readonly controlType = 'radio-group';

  @Input() id = '';

  /**
   * Fires when the selected value changes. Mirrors the native per-radio
   * `(change)` so sites with a side-effect on selection (and no `[(ngModel)]`
   * to give them `(ngModelChange)`) can react declaratively.
   */
  @Output() valueChange = new EventEmitter<SuiRadioValue | null>();

  @Input()
  set disabled(val: boolean) {
    this.setDisabledState(val);
  }
  get disabled(): boolean {
    return this.isDisabled();
  }

  get effectiveId(): string {
    return this.id;
  }

  get focused(): boolean {
    return this._focused;
  }

  get empty(): boolean {
    return !this.value();
  }

  get errorState(): boolean {
    return !!(this.ngControl?.invalid && this.ngControl?.touched);
  }

  constructor() {
    super();
    effect(() => this.radioGroupState()?.value.set(this.value()));
    effect(() => this.radioGroupState()?.disabled.set(this.isDisabled()));
  }

  ngOnInit(): void {
    this.subscription = this.ngpRadioGroup.valueChange.subscribe((val) => {
      // `NgpRadioItem` selects on `focus`, so *any* programmatic focus writes to
      // the model — a popover/dialog focus-trap autofocusing the first item would
      // silently select it (and, on a one-way `[ngModel]`, dispatch a real change).
      // Only a gesture inside the group counts; otherwise put our value back,
      // since ng-primitives has already moved its own state.
      if (!this._userInitiated) {
        this.radioGroupState()?.value.set(this.value());
        return;
      }

      this.setValue(val);
      this.valueChange.emit(val);
    });
  }

  /** Latches on the first real gesture: pointer press, or a key (arrows/space). */
  protected markUserInitiated(): void {
    this._userInitiated = true;
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._focused = true;
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this._focused = false;
    this.markTouched();
  }
}
